/**
 * Crop type -> harvest (yield) type -> feed type mapping.
 *
 * Farmers grow crops that are conserved as forage and fed back to animals, so
 * the three taxonomies need to line up:
 *
 *   crop grown (ARABLE_TYPE / grass)  ->  harvested as (yield type)  ->  fed as (feed type)
 *
 * Grass fields are always harvested as grass silage / hay / haylage and are
 * handled directly in the yield form; this module covers arable crops and the
 * shared harvest -> feed translation used when tracking feed stock.
 */
import {
  ARABLE_TYPE,
  CROP_FORAGE,
  CROP_YIELD_TYPE,
  GRASS_YIELD_TYPES,
} from "./commonTypes";
import { GENERAL_FEED_TYPE } from "./feedStockTypes";

/**
 * For each arable crop, the harvest (yield) types that can be taken off it.
 * The crop's own name represents the grain/primary yield.
 */
export const CROP_HARVEST_OPTIONS: Record<ARABLE_TYPE, CROP_YIELD_TYPE[]> = {
  [ARABLE_TYPE.WHEAT]: [
    ARABLE_TYPE.WHEAT,
    CROP_FORAGE.WHOLECROP_SILAGE,
    CROP_YIELD_TYPE.STRAW,
  ],
  [ARABLE_TYPE.BARLEY]: [
    ARABLE_TYPE.BARLEY,
    CROP_FORAGE.WHOLECROP_SILAGE,
    CROP_YIELD_TYPE.STRAW,
  ],
  [ARABLE_TYPE.CEREALS]: [
    ARABLE_TYPE.CEREALS,
    CROP_FORAGE.WHOLECROP_SILAGE,
    CROP_YIELD_TYPE.STRAW,
  ],
  // Forage maize is harvested as maize silage; grain maize is rarer but kept.
  [ARABLE_TYPE.MAIZ]: [CROP_FORAGE.MAIZE_SILAGE, ARABLE_TYPE.MAIZ],
  [ARABLE_TYPE.OILSEED_RAPE]: [ARABLE_TYPE.OILSEED_RAPE, CROP_YIELD_TYPE.STRAW],
  [ARABLE_TYPE.POTATO]: [ARABLE_TYPE.POTATO],
  [ARABLE_TYPE.VEGETABLES_SALAD]: [ARABLE_TYPE.VEGETABLES_SALAD],
  [ARABLE_TYPE.OTHERS]: [ARABLE_TYPE.OTHERS],
};

/**
 * Which feed type a given harvest becomes once it's in stock. Harvests not
 * listed here are not used as animal feed (e.g. potatoes, vegetables).
 * Keyed by string so it can be indexed by any grass or crop yield type.
 */
export const HARVEST_TO_FEED: Partial<Record<string, GENERAL_FEED_TYPE>> = {
  [GRASS_YIELD_TYPES.SILAGE]: GENERAL_FEED_TYPE.SILAGE,
  [GRASS_YIELD_TYPES.HAY]: GENERAL_FEED_TYPE.HAY,
  [GRASS_YIELD_TYPES.HAYLAGE]: GENERAL_FEED_TYPE.HAYLAGE,
  [CROP_FORAGE.MAIZE_SILAGE]: GENERAL_FEED_TYPE.MAIZE_SILAGE,
  [CROP_FORAGE.WHOLECROP_SILAGE]: GENERAL_FEED_TYPE.WHOLECROP_SILAGE,
  // Straw is intentionally not a feed-stock source: harvested straw is treated
  // as bedding/roughage (consistent with the FeedInStore cube). STRAW is still
  // a valid feed type for purchased/fed straw.
  [ARABLE_TYPE.WHEAT]: GENERAL_FEED_TYPE.GRAIN,
  [ARABLE_TYPE.BARLEY]: GENERAL_FEED_TYPE.GRAIN,
  [ARABLE_TYPE.CEREALS]: GENERAL_FEED_TYPE.GRAIN,
  [ARABLE_TYPE.MAIZ]: GENERAL_FEED_TYPE.GRAIN,
  [ARABLE_TYPE.OILSEED_RAPE]: GENERAL_FEED_TYPE.GRAIN,
};
